// useCommentThread.ts — orchestration hook for the post-detail comment tree.
//
// Composes useComments(postId) + useSession() — never imports stores/services/data directly.
// Owns: sort, expand, single-open inline reply (+ draft), single-open inline edit,
//       delete-confirm, show-all pagination.

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useDiscussions } from '@/hooks/useDiscussions';
import { discussionsService } from '@/core/services/discussionsService';
import { useComments, useSession, useCommentsHydration } from '@/hooks';
import type { Comment } from '@/core/models/comment';
import { parseRelativeTime } from './time';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SortId = 'most-relevant' | 'most-liked' | 'newest' | 'oldest';

export interface CommentThread {
  comments: Comment[];
  reveal: (id: string) => boolean;
  sort: SortId;
  setSort: (s: SortId) => void;
  liveCount: number;
  visibleThreads: Comment[]; // top-level, sorted + capped (unless showAll)
  hiddenCount: number; // sorted top-level beyond the cap
  showAll: boolean;
  setShowAll: (v: boolean) => void;
  repliesFor: (topId: string) => Comment[];
  isExpanded: (topId: string) => boolean;
  toggleExpanded: (topId: string) => void;
  // reply (single-open)
  openReplyId: string | null;
  replyTargetHandle: string | null;
  replyDraft: string;
  openReply: (target: Comment, targetHandle?: string | null) => void;
  cancelReply: () => void;
  setReplyDraft: (d: string) => void;
  submitReply: (body: string) => Promise<void>;
  // edit (single-open)
  editingId: string | null;
  startEdit: (id: string) => void;
  cancelEdit: () => void;
  saveEdit: (id: string, body: string, reason?: string) => Promise<void>;
  // delete
  deleteTargetId: string | null;
  requestDelete: (id: string) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
  // top-level composer
  addTopLevel: (body: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOP_LEVEL_CAP = 5;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCommentThread(postId: string): CommentThread {
  const discussions = useDiscussions();
  const sortChosen = useRef(false);
  useCommentsHydration(postId);
  const {
    comments,
    topLevel,
    repliesFor: repliesForRaw,
    addComment,
    addReply,
    updateComment,
    deleteComment,
  } = useComments(postId);

  const { currentUser } = useSession();
  const authorHandle = currentUser?.handle ?? '@guest';

  // --- sort ---
  const [sort, changeSort] = useState<SortId>('most-relevant');
  const setSort = (value: SortId) => {
    sortChosen.current = true;
    changeSort(value);
  };
  useEffect(() => {
    if (discussions.ready && !sortChosen.current) changeSort(discussions.prefs.commentSort);
  }, [discussions.ready, discussions.prefs.commentSort]);

  // --- expand ---
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());

  // --- reply state ---
  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);
  const [replyTargetHandle, setReplyTargetHandle] = useState<string | null>(null);
  const [replyHadHandle, setReplyHadHandle] = useState<boolean>(false);
  const [replyDraft, setReplyDraft] = useState<string>('');

  // --- edit state ---
  const [editingId, setEditingId] = useState<string | null>(null);

  // --- delete state ---
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // --- show-all ---
  const [showAll, setShowAll] = useState<boolean>(false);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const liveCount = useMemo(() => comments.filter((c) => !c.deleted).length, [comments]);

  const sortedTopLevel = useMemo(() => {
    if (sort === 'most-liked') return [...topLevel].sort((a, b) => b.likes - a.likes);
    if (sort === 'newest') {
      return [...topLevel].sort((a, b) =>
        a.createdAt && b.createdAt
          ? Date.parse(b.createdAt) - Date.parse(a.createdAt)
          : parseRelativeTime(a.time) - parseRelativeTime(b.time),
      );
    }
    if (sort === 'oldest') {
      return [...topLevel].sort((a, b) =>
        a.createdAt && b.createdAt
          ? Date.parse(a.createdAt) - Date.parse(b.createdAt)
          : parseRelativeTime(b.time) - parseRelativeTime(a.time),
      );
    }
    // most-relevant: store order as-is
    return topLevel;
  }, [topLevel, sort]);

  const visibleThreads = useMemo(
    () => (showAll ? sortedTopLevel : sortedTopLevel.slice(0, TOP_LEVEL_CAP)),
    [sortedTopLevel, showAll],
  );

  const hiddenCount = Math.max(0, sortedTopLevel.length - TOP_LEVEL_CAP);

  // ---------------------------------------------------------------------------
  // Callbacks
  // ---------------------------------------------------------------------------

  const repliesFor = useCallback((topId: string) => repliesForRaw(topId), [repliesForRaw]);

  const isExpanded = useCallback((id: string) => expandedSet.has(id), [expandedSet]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // --- reply ---

  const openReply = useCallback(
    (target: Comment, targetHandle?: string | null) => {
      // Draft-discard guard: if switching to a different comment with a non-empty draft, confirm.
      if (openReplyId !== null && openReplyId !== target.id && replyDraft.trim() !== '') {
        if (!window.confirm('Discard your draft?')) return;
      }

      const isTopLevel = target.parentId === null;
      // Flatten rule: parentId is always the top-level ancestor
      const parentId = isTopLevel ? target.id : target.parentId!;
      // replyingToHandle only when replying to a nested comment
      const hadHandle = !isTopLevel;
      const resolvedHandle = hadHandle ? (targetHandle ?? null) : null;

      setOpenReplyId(target.id);
      setReplyParentId(parentId);
      setReplyHadHandle(hadHandle);
      setReplyTargetHandle(resolvedHandle);
      setReplyDraft('');
    },
    [openReplyId, replyDraft],
  );

  const cancelReply = useCallback(() => {
    setOpenReplyId(null);
    setReplyParentId(null);
    setReplyTargetHandle(null);
    setReplyHadHandle(false);
    setReplyDraft('');
  }, []);

  const submitReply = useCallback(
    async (body: string) => {
      if (!openReplyId || !replyParentId) return;

      await addReply({
        postId,
        parentId: replyParentId,
        targetCommentId: openReplyId,
        body,
        authorHandle,
        // undefined (not null) when replying to a top-level comment — drives the
        // flatten rule: no attribution handle for direct top-level replies.
        replyingToHandle: replyHadHandle ? (replyTargetHandle ?? undefined) : undefined,
      });

      // Auto-expand the parent thread
      setExpandedSet((prev) => {
        const next = new Set(prev);
        next.add(replyParentId);
        return next;
      });

      cancelReply();
      if (discussions.owner) void discussionsService.load(discussions.owner, true);
    },
    [
      openReplyId,
      replyParentId,
      replyHadHandle,
      replyTargetHandle,
      addReply,
      postId,
      authorHandle,
      cancelReply,
      discussions.owner,
    ],
  );

  // --- edit ---

  const startEdit = useCallback((id: string) => {
    setEditingId(id);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const saveEdit = useCallback(
    async (id: string, body: string, reason?: string) => {
      const saved = reason ? await updateComment(id, body, reason) : await updateComment(id, body);
      if (!saved) return;
      setEditingId(null);
    },
    [updateComment],
  );

  // --- delete ---

  const requestDelete = useCallback((id: string) => {
    setDeleteTargetId(id);
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteTargetId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId) return;
    await deleteComment(deleteTargetId);
    setDeleteTargetId(null);
  }, [deleteTargetId, deleteComment]);

  // --- top-level composer ---

  const addTopLevel = useCallback(
    async (body: string) => {
      await addComment({ postId, body, authorHandle });
      if (discussions.owner) void discussionsService.load(discussions.owner, true);
    },
    [addComment, postId, authorHandle, discussions.owner],
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    comments,
    reveal: (id: string) => {
      const comment = comments.find((c) => c.id === id && !c.deleted);
      if (!comment) return false;
      setShowAll(true);
      if (comment.parentId) setExpandedSet((prev) => new Set([...prev, comment.parentId!]));
      return true;
    },
    sort,
    setSort,
    liveCount,
    visibleThreads,
    hiddenCount,
    showAll,
    setShowAll,
    repliesFor,
    isExpanded,
    toggleExpanded,
    openReplyId,
    replyTargetHandle,
    replyDraft,
    openReply,
    cancelReply,
    setReplyDraft,
    submitReply,
    editingId,
    startEdit,
    cancelEdit,
    saveEdit,
    deleteTargetId,
    requestDelete,
    cancelDelete,
    confirmDelete,
    addTopLevel,
  };
}
